import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../config/firebase';
import { VeraWordmark } from '../components/icons/VeraWordmark';

export const AuthAction: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    const verifyCode = async () => {
      if (mode !== 'resetPassword' || !oobCode) {
        setError('Invalid password reset link');
        setVerifying(false);
        return;
      }

      try {
        // Verify the password reset code and get the user's email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setVerifying(false);
      } catch (err: any) {
        console.error('Error verifying reset code:', err);
        if (err.code === 'auth/expired-action-code') {
          setError('This password reset link has expired. Please request a new one.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('This password reset link is invalid or has already been used.');
        } else {
          setError('Invalid or expired password reset link');
        }
        setVerifying(false);
      }
    };

    verifyCode();
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!oobCode) {
      setError('Invalid reset code');
      return;
    }

    setLoading(true);

    try {
      // Confirm the password reset with the new password
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Error resetting password:', err);
      if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This password reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This password reset link is invalid or has already been used.');
      } else {
        setError(err.message || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying the code
  if (verifying) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--color-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-4)',
        }}
      >
        <div className="card" style={{ width: '100%', maxWidth: '450px', textAlign: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <VeraWordmark size={48} />
          </div>
          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--spacing-4)' }}>
            Verifying Reset Link...
          </h1>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4F6367]"></div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--color-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-4)',
        }}
      >
        <div className="card" style={{ width: '100%', maxWidth: '450px', textAlign: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <VeraWordmark size={48} />
          </div>

          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'var(--color-success-light, #dcfce7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto var(--spacing-4)',
            }}
          >
            <svg
              style={{ width: '32px', height: '32px', color: 'var(--color-success, #22c55e)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--spacing-2)' }}>
            Password Reset Successful!
          </h1>

          <p
            style={{
              color: 'var(--color-gray-600)',
              marginBottom: 'var(--spacing-6)',
              fontSize: 'var(--text-base)',
            }}
          >
            Your password has been successfully reset. You will be redirected to the login page in a few seconds.
          </p>

          <button
            onClick={() => navigate('/login')}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  // Error state (invalid or expired link)
  if (error && !email) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: 'var(--color-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--spacing-4)',
        }}
      >
        <div className="card" style={{ width: '100%', maxWidth: '450px', textAlign: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <VeraWordmark size={48} />
          </div>

          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--spacing-4)' }}>
            Invalid Reset Link
          </h1>

          <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-6)' }}>
            {error}
          </div>

          <button
            onClick={() => navigate('/forgot-password')}
            className="btn btn-primary"
            style={{ width: '100%', marginBottom: 'var(--spacing-3)' }}
          >
            Request New Reset Link
          </button>

          <button
            onClick={() => navigate('/login')}
            className="btn"
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              color: 'var(--color-secondary)',
              border: '1px solid var(--color-gray-300)',
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Password reset form
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-primary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-4)',
      }}
    >
      <div className="card" style={{ width: '100%', maxWidth: '450px' }}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-6)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <VeraWordmark size={48} />
          </div>
          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--spacing-2)' }}>
            Create New Password
          </h1>
          <p style={{ color: 'var(--color-gray-600)', fontSize: 'var(--text-sm)' }}>
            Resetting password for <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="newPassword">
              New Password *
            </label>
            <input
              id="newPassword"
              type="password"
              className="form-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              minLength={8}
              autoFocus
            />
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', marginTop: 'var(--spacing-1)' }}>
              Must be at least 8 characters
            </p>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="confirmPassword">
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              type="password"
              className="form-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={8}
            />
          </div>

          {error && (
            <div className="alert alert-error" style={{ marginBottom: 'var(--spacing-4)' }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ width: '100%', marginBottom: 'var(--spacing-4)' }}
          >
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};
